import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ConfirmModal, useToast } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { CourseActionsMenu } from '@/components/courses/CourseActionsMenu';
import { roleActionErrorToast } from '@/lib/user-feedback';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  published: 'Publicado',
  archived: 'Archivado',
};

export interface CourseListItem {
  id: string;
  name: string;
  code?: string | null;
  status?: string;
}

interface CourseListCardProps {
  course: CourseListItem;
  categoryId: string;
  courseSearch: string;
}

function courseReturnQuery(categoryId: string, courseSearch: string) {
  const params = new URLSearchParams();
  params.set('return_to', 'categories');
  params.set('category_id', categoryId);
  if (courseSearch) params.set('course_search', courseSearch);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function CourseListCard({ course, categoryId, courseSearch }: CourseListCardProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission(PERMISSIONS.CURSOS_ELIMINAR);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const returnQuery = courseReturnQuery(categoryId, courseSearch);
  const status = course.status ?? 'draft';

  const meta = [course.code ? `ID: ${course.code}` : null, STATUS_LABELS[status] ?? status]
    .filter(Boolean)
    .join(' • ');

  const removeCourse = useMutation({
    mutationFn: () => api(`/courses/${course.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteOpen(false);
      queryClient.invalidateQueries({ queryKey: ['courses', 'category', categoryId] });
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
    onError: (error) => {
      setDeleteOpen(false);
      toast(roleActionErrorToast(error, 'No se pudo eliminar el curso'));
    },
  });

  return (
    <li className="ccm-course-card">
      <div className="ccm-course-card__main">
        <div className="ccm-course-card__icon" aria-hidden="true">
          <span className="material-symbols-outlined text-[28px]">auto_stories</span>
        </div>
        <div className="min-w-0">
          <Link to={`/courses/${course.id}${returnQuery}`} className="ccm-course-card__title hover:text-[#142175]">
            {course.name}
          </Link>
          {meta && <p className="ccm-course-card__meta">{meta}</p>}
        </div>
      </div>

      <div className="ccm-course-card__actions">
        <CourseActionsMenu
          courseId={course.id}
          courseName={course.name}
          status={status}
          returnQuery={returnQuery}
          variant="gear"
          direction="down"
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['courses', 'category', categoryId] })}
        />

        <Link
          to={`/courses/${course.id}${returnQuery}`}
          className="ccm-round-btn"
          title="Vista previa"
          aria-label={`Vista previa de ${course.name}`}
        >
          <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
            visibility
          </span>
        </Link>

        {canDelete && (
          <button
            type="button"
            className="ccm-round-btn ccm-round-btn--danger"
            title="Eliminar"
            aria-label={`Eliminar ${course.name}`}
            onClick={() => setDeleteOpen(true)}
          >
            <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
              delete
            </span>
          </button>
        )}
      </div>

      <ConfirmModal
        open={deleteOpen}
        title="Eliminar curso"
        description={`¿Eliminar el curso «${course.name}»? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        loading={removeCourse.isPending}
        onConfirm={() => removeCourse.mutate()}
        onClose={() => setDeleteOpen(false)}
      />
    </li>
  );
}
